import React, { ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { AppPage, LoadingMask } from ".";
import { ActionType } from "../@types";
import { getLoggedInBeneInfo } from "../api";
import routes from "../app/routes";
import { AppContext } from "../app/store";
import {
  beneKeysMatch,
  beneLoginAndUpdateHandler,
  useTranslate,
  logout,
  getIsSlsxAuthenticated,
  hasGlobalSession,
  hasLocalSession,
} from "../helpers";
import { ApiResponseError } from "../helpers/errors";
import { handleMbpLandingRoute } from "../helpers/routeHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { erase } from "browser-cookies";

// @TODO - Re-evaluate in MCT-7039
// Taken from now-defunct `UserActivityMonitor`
const doNotRetrieveMissingBeneRoutes: string[] = [
  routes.csrLanding,
  routes.mbpLandingPage,
  routes.slsCallback,
];

export const GlobalSessionHandler = ({ children }: { children: ReactNode }) => {
  const {
    state: { beneficiary, mbpHandoffFailed, csr },
    state,
    dispatch,
  } = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const isCsr = csr || location.pathname === routes.csrLanding;
  const flags = useFlags();
  const { feEnableAuthentication } = flags;
  const [loading, setLoading] = useState(
    // Start with loading state set to true for callback routes
    // Callback routes always use the `redirect_uri` to send the user somewhere else
    isLoginCallbackRoute || isMbpLandingRoute
  );
  const t = useTranslate();

  useEffect(() => {
    (async () => {
      if (!feEnableAuthentication || isCsr) {
        return setLoading(false);
      }

      // @TODO - MBP Landing page should be handled similarly to login callback route,
      // and should share logic, as much as possible
      if (isMbpLandingRoute) {
        console.debug(
          `🐪 MBP landing route. mbpHandoffFailed? ${mbpHandoffFailed}`
        );
        dispatch({
          type: ActionType.RESET_STATE,
        });
        return handleMbpLandingRoute({
          dispatch,
          search: location.search,
          history,
          state,
          flags,
        }).then(renderChildren => renderChildren && setLoading(false));
      }

      console.debug(
        `🐝 Inside GlobalSessionHandler, logged in? ${hasGlobalSession(state)}`
      );

      try {
        // Check whether the user is authenticated. If not, throwing here will
        // let the user continue and actively logout if state needs clearing
        if (!isLoginCallbackRoute && !(await getIsSlsxAuthenticated())) {
          throw new Error("No global session or error getting global session");
        }
        // @TODO - All non-CSR routes now call `getLoggedInBeneInfo` (`/beneinfo/userinfo`)
        // which needs no values from a `req_token` query param.
        // Try to unify the handling of all auth routes, as much as possible
        //
        // Get latest beneInfo
        const beneInfoWithHeaders = await getLoggedInBeneInfo();
        const { beneficiary: beneInfo } = beneInfoWithHeaders;
        // For non-login page loads, if there's bene info already in app state,
        // and the cached bene and fetched bene are the same, just update bene info and return
        if (
          !isLoginCallbackRoute &&
          beneficiary &&
          beneInfo &&
          beneKeysMatch(beneficiary, beneInfo)
        ) {
          console.debug(
            `🌸 LI bene page reload, returning from GlobalSessionHandler, continuing authenticated`
          );
          return;
        }

        // @TODO - Re-evaluate in MCT-7039
        // Taken from now-defunct `UserActivityMonitor`
        // Pushes a location to history for non-callback routes
        // May be used in the `beneLoginAndUpdateHandler`
        const doNotSetHistoryStateFromRoute =
          doNotRetrieveMissingBeneRoutes.includes(location.pathname);
        if (
          hasLocalSession() &&
          !beneficiary &&
          !doNotSetHistoryStateFromRoute
        ) {
          const historyStateFromRoute = {
            pathname: `${routes.slsCallback}`,
            state: { from: `${location.pathname}${location.search}` },
          };
          history.push(historyStateFromRoute);
        }

        // At this point, the user has a session, but we may need to fully update
        // the store for a LI bene experience. This logic applies to the
        // `loginCallbackRoute` and potentially other routes, if coming to MCT for
        // the first time from an authenticated MBP session
        setLoading(true);
        await beneLoginAndUpdateHandler({
          state,
          dispatch,
          history,
          location,
          flags,
          beneInfoWithHeaders,
        });
        setLoading(false);
      } catch (e) {
        console.debug(`🦄 Could not fecth a bene in GlobalSessionHandler`);
        let apiResponseStatus;
        if (e instanceof ApiResponseError) {
          const { info } = e;
          apiResponseStatus = info.apiResponseStatus;
        }
        // If app state (store) still holds bene info, we need to actively log
        // out, locally, clear everything out, and return to non-LI landing
        if (beneficiary) {
          console.debug(`🦞 Actively logging out`);
          logout({
            dispatch,
            apiExpired: apiResponseStatus === 401,
            clientExpired: true,
          });
        }
        if (hasLocalSession()) {
          // expire `sid` cookie, if it's still hanging around
          erase("sid");
        }
        // This likely means there is no session, and the page should proceed to
        // load unauthenticated
        // @TODO - May need to add conditional toast/routing here, even if there
        // is no bene in app state
        console.debug(`🐠 Continuing unauthenticated`);
        setLoading(false);
      }
    })();
  }, [isLoginCallbackRoute, isMbpLandingRoute, isCsr, feEnableAuthentication]);

  return loading ? (
    <AppPage>
      <LoadingMask
        loading={true}
        message={t("loading.retrieving_your_information")}
      />
    </AppPage>
  ) : (
    <>{children}</>
  );
};
